/**
 * Next.js 16 Activity keeps prior /notebook/* trees in the DOM.
 * On re-entry a second .nb-root mounts → blocks overlap in the same space.
 */

import { resetNotebookEditorSurface } from './tiptap-lifecycle'
import { nbLog, nbWarn } from './notebook-debug'

const ROOT_SELECTOR = '.nb-root[data-nb-route-root]'

export function getActiveNotebookRoot() {
  if (typeof document === 'undefined') return null
  return document.querySelector(`${ROOT_SELECTOR}[data-nb-route-active="true"]`)
}

export function evictStaleNotebookRoots(keepRoot) {
  if (typeof document === 'undefined' || !keepRoot) return

  const roots = [...document.querySelectorAll(ROOT_SELECTOR)]
  const stale = roots.filter((root) => root !== keepRoot)

  keepRoot.dataset.nbRouteActive = 'true'
  keepRoot.removeAttribute('hidden')
  keepRoot.removeAttribute('inert')
  keepRoot.removeAttribute('aria-hidden')
  keepRoot.style.removeProperty('display')

  if (stale.length === 0) return

  for (const root of stale) {
    root.remove()
  }

  resetNotebookEditorSurface()

  nbWarn('Removed stale notebook Activity roots', {
    removed: stale.length,
    kept: keepRoot.getAttribute('data-nb-route-root-id'),
  })
}

/** Hide root when Activity pauses this route — uses existing .nb-root CSS, no layout changes. */
export function deactivateNotebookRoot(root) {
  if (!root) return
  root.dataset.nbRouteActive = 'false'
  root.setAttribute('hidden', '')
  root.setAttribute('aria-hidden', 'true')
  root.setAttribute('inert', '')
  nbLog('nav', 'notebook root deactivated', {
    id: root.getAttribute('data-nb-route-root-id'),
  })
}

/**
 * Run on every client navigation into a notebook.
 * @param {string | null | undefined} [preferNotebookId]
 */
export function purgeOrphanNotebookRoots(preferNotebookId = null) {
  if (typeof document === 'undefined') return

  const roots = [...document.querySelectorAll(ROOT_SELECTOR)]
  if (roots.length <= 1) return

  const matchingId = preferNotebookId
    ? roots.filter((r) => r.getAttribute('data-nb-notebook-id') === preferNotebookId)
    : []

  /** Always prefer the newest mount — Activity preserves older trees first in document order. */
  const keep =
    matchingId[matchingId.length - 1] ??
    roots[roots.length - 1]

  for (const root of roots) {
    if (root !== keep) {
      deactivateNotebookRoot(root)
      root.remove()
    }
  }

  keep.dataset.nbRouteActive = 'true'
  keep.removeAttribute('hidden')
  keep.removeAttribute('inert')
  keep.removeAttribute('aria-hidden')
  keep.style.removeProperty('display')

  resetNotebookEditorSurface()

  nbWarn('Purged orphan notebook roots', {
    removed: roots.length - 1,
    preferNotebookId,
    kept: keep.getAttribute('data-nb-route-root-id'),
  })
}

/** Remove every notebook root — run when leaving /notebook/* so Activity cannot leave ghost DOM. */
export function purgeAllNotebookRoots() {
  if (typeof document === 'undefined') return 0

  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/notebook/')) {
    nbLog('nav', 'purgeAll skipped — pathname is still /notebook/*')
    return 0
  }

  const roots = [...document.querySelectorAll(ROOT_SELECTOR)]
  if (roots.length === 0) return 0

  for (const root of roots) {
    root.remove()
  }

  resetNotebookEditorSurface()

  nbWarn('Purged all notebook roots on route exit', { removed: roots.length })
  return roots.length
}

function readEpochAttr(el, attr) {
  const n = Number(el?.getAttribute(attr) ?? 0)
  return Number.isFinite(n) ? n : 0
}

function pickLatestByEpoch(elements, attr) {
  if (!elements.length) return null
  return elements.reduce((best, el) =>
    readEpochAttr(el, attr) >= readEpochAttr(best, attr) ? el : best,
  )
}

/**
 * Remove preserved SPA subtrees inside the active root (Activity / keyed remounts).
 * Keeps the latest entry wrapper + route mount; drops older live duplicates.
 *
 * @param {Element | null | undefined} [keepRouteMount]
 * @returns {{ removedEntries: number, removedMounts: number }}
 */
export function evictStaleNotebookRouteMounts(keepRouteMount = null) {
  if (typeof document === 'undefined') {
    return { removedEntries: 0, removedMounts: 0 }
  }

  const root = getActiveNotebookRoot()
  if (!root) return { removedEntries: 0, removedMounts: 0 }

  let removedEntries = 0
  let removedMounts = 0

  const entryWrappers = [...root.querySelectorAll('[data-nb-route-entry]')]
  const keepEntry = pickLatestByEpoch(entryWrappers, 'data-nb-route-entry')
  for (const el of entryWrappers) {
    if (el !== keepEntry) {
      el.remove()
      removedEntries += 1
    }
  }

  const scope = keepEntry ?? root
  const routeMounts = [...scope.querySelectorAll('.nb-route-mount')]
  const keepMount =
    keepRouteMount && routeMounts.includes(keepRouteMount)
      ? keepRouteMount
      : routeMounts.find(
          (m) => m.getAttribute('data-nb-route-active-mount') === 'true',
        ) ??
        pickLatestByEpoch(routeMounts, 'data-nb-route-visit') ??
        routeMounts[routeMounts.length - 1] ??
        null

  for (const mount of routeMounts) {
    if (mount !== keepMount) {
      mount.remove()
      removedMounts += 1
    }
  }

  if (removedEntries > 0 || removedMounts > 0) {
    resetNotebookEditorSurface()
    nbWarn('Removed stale notebook route subtrees inside active root', {
      removedEntries,
      removedMounts,
      keepEntryEpoch: keepEntry?.getAttribute('data-nb-route-entry'),
      keepRouteVisit: keepMount?.getAttribute('data-nb-route-visit'),
    })
  }

  return { removedEntries, removedMounts }
}
